import { useState, useEffect, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { Toaster, toast } from 'sonner'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { SettingsProvider } from './contexts/SettingsContext'
import { SubscriptionProvider } from './contexts/SubscriptionContext'
import { LocaleProvider } from './contexts/LocaleContext'
import AppLayout from './components/AppLayout'
import Dashboard from './pages/Dashboard'
import TemplatesPage, { addSavedTemplate } from './pages/TemplatesPage'
import { Client } from './lib/api'
import LoginPage from './pages/LoginPage'
import LandingPage from './pages/LandingPage'
import NewProposalModal from './components/NewProposalModal'
import PrivacyConsentPopup from './components/PrivacyConsentPopup'
import UpgradeModal from './components/UpgradeModal'
import OnboardingWizard, { OnboardingData } from './components/OnboardingWizard'
import { useSubscription } from './contexts/SubscriptionContext'
import { Block } from './components/BlockEditor'
import aiService from './lib/ai'
import ErrorBoundary from './components/ErrorBoundary'

// Lazy-loaded pages (code split for faster initial load)
const ProposalEditor = lazy(() => import('./pages/ProposalEditor'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const ClientsPage = lazy(() => import('./pages/ClientsPage'))
const ClientProfilePage = lazy(() => import('./pages/ClientProfilePage'))
const BillingPage = lazy(() => import('./pages/BillingPage'))
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))
const InboxPage = lazy(() => import('./pages/InboxPage'))
const ConnectPage = lazy(() => import('./pages/ConnectPage'))
const PublicProposalView = lazy(() => import('./pages/PublicProposalView'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const AccessibilityPage = lazy(() => import('./pages/AccessibilityPage'))
const HatatMehirPage = lazy(() => import('./pages/HatatMehirPage'))
const HatimaDigitalitPage = lazy(() => import('./pages/HatimaDigitalitPage'))
const ProposalTemplatePage = lazy(() => import('./pages/ProposalTemplatePage'))

const PageSpinner = () => (
  <div className="min-h-screen bg-gray-100 flex items-center justify-center" dir="ltr">
    <div className="w-12 h-12 border-4 border-accent-500 border-t-transparent rounded-full animate-spin" />
  </div>
)

type AppView = 'dashboard' | 'editor' | 'templates' | 'settings' | 'profile' | 'clients' | 'client-profile' | 'billing' | 'admin' | 'email' | 'integrations'

function ProtectedApp() {
  const { user, loading } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [currentView, setCurrentView] = useState<AppView>('dashboard')
  const [initialBlocks, setInitialBlocks] = useState<Block[]>([])
  const [editingProposalId, setEditingProposalId] = useState<string | null>(null)
  const [showNewProposalModal, setShowNewProposalModal] = useState(false)
  const [isGeneratingAI, setIsGeneratingAI] = useState(false)
  const [initialClientMeta, setInitialClientMeta] = useState<{
    clientName: string
    clientEmail: string
    clientCompany: string
    clientId?: string
  } | null>(null)
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [upgradeFeature, setUpgradeFeature] = useState<string | undefined>()
  const { canCreateProposal, canUseAI, canUseTemplates } = useSubscription()

  // Handle PayPal subscription return
  useEffect(() => {
    const subscriptionId = searchParams.get('subscription_id')
    const activated = searchParams.get('subscription_activated')

    if (activated === '1' && subscriptionId && user) {
      // User returned from PayPal - activate subscription
      import('./lib/api').then(({ default: apiClient }) => {
        apiClient.activateSubscription(subscriptionId).then((res) => {
          const planName = res.plan ? res.plan.charAt(0).toUpperCase() + res.plan.slice(1) : 'Pro'
          toast.success(`Welcome to Quor ${planName}!`, {
            description: 'Your subscription is now active. A confirmation email has been sent.',
            duration: 6000,
          })
          setSearchParams({})
          // Reload after short delay so toast is visible
          setTimeout(() => window.location.reload(), 2000)
        }).catch(() => {
          toast.error('Failed to activate subscription. Please contact support.')
          setSearchParams({})
        })
      })
    } else if (searchParams.get('subscription_cancelled') === '1') {
      toast.info('Subscription cancelled. You can upgrade anytime.')
      setSearchParams({})
    } else if (activated === '1') {
      setSearchParams({})
    }
  }, [searchParams, user, setSearchParams])

  // Check if user needs onboarding (first-time setup)
  // Check via API if profile is complete, not localStorage
  useEffect(() => {
    if (user && !loading) {
      // First check localStorage for quick UX (avoid flash)
      const onboarded = localStorage.getItem(`quor_onboarded_${user.id}`)
      if (onboarded) return

      // Then verify with server - profile needs full_name to be considered complete
      import('./lib/api').then(({ default: apiClient }) => {
        apiClient.getMe().then((response) => {
          const data = response as { profile?: { full_name?: string, business_name?: string } }
          const hasCompletedProfile = data?.profile?.full_name && data?.profile?.business_name
          if (!hasCompletedProfile) {
            setShowOnboarding(true)
          } else {
            // Profile exists, mark as onboarded locally for faster checks
            localStorage.setItem(`quor_onboarded_${user.id}`, '1')
          }
        }).catch(() => {
          // On error, show onboarding to be safe
          setShowOnboarding(true)
        })
      })
    }
  }, [user, loading])

  const handleOnboardingComplete = async (data: OnboardingData) => {
    if (user) {
      localStorage.setItem(`quor_onboarded_${user.id}`, '1')
      // Save profile data via API
      try {
        const { default: apiClient } = await import('./lib/api')
        await apiClient.updateProfile({
          full_name: data.fullName,
          business_name: data.businessName,
          business_type: data.businessType,
          primary_color: data.primaryColor,
          _send_welcome: true,
        })
        toast.success('Profile saved successfully!')
      } catch (err) {
        console.error('Failed to save onboarding data:', err)
        toast.error('Failed to save profile, please try again in Settings')
      }
    }
    setShowOnboarding(false)
  }

  const promptUpgrade = (feature: string) => {
    setUpgradeFeature(feature)
    setShowUpgradeModal(true)
  }

  // Open the new proposal selection modal
  const handleOpenNewProposalModal = () => {
    if (!canCreateProposal) {
      promptUpgrade('Unlimited proposal creation')
      return
    }
    setShowNewProposalModal(true)
  }

  // Handle new proposal - shows modal if no blocks, or goes to editor with blocks
  const handleNewProposal = (blocks?: Block[]) => {
    if (!canCreateProposal) {
      promptUpgrade('Unlimited proposal creation')
      return
    }
    if (blocks && blocks.length > 0) {
      setInitialBlocks(blocks)
      setEditingProposalId(null)
      setCurrentView('editor')
    } else {
      setShowNewProposalModal(true)
    }
  }

  // Start from scratch - go directly to editor
  const handleStartFromScratch = () => {
    setShowNewProposalModal(false)
    setInitialBlocks([])
    setEditingProposalId(null)
    setInitialClientMeta(null)
    setCurrentView('editor')
  }

  // Use template - go to templates page
  const handleGoToTemplates = () => {
    if (!canUseTemplates) {
      setShowNewProposalModal(false)
      promptUpgrade('Ready-made templates')
      return
    }
    setShowNewProposalModal(false)
    setCurrentView('templates')
  }

  // Helper: create fallback blocks when AI fails or returns empty
  const createFallbackBlocks = (prompt: string, errorMessage?: string): Block[] => {
    const now = Date.now()
    return [
      {
        id: `block-${now}-1`,
        type: 'cover',
        order: 0,
        title: 'Proposal',
        subtitle: prompt.length > 100 ? prompt.slice(0, 100) + '...' : prompt,
        overlay: true
      } as Block,
      {
        id: `block-${now}-2`,
        type: 'heading',
        order: 1,
        content: 'Proposal Details',
        level: 2
      } as Block,
      {
        id: `block-${now}-3`,
        type: 'text',
        order: 2,
        content: errorMessage
          ? `${errorMessage}\n\nProject description: ${prompt}`
          : `This proposal was created based on the request: "${prompt}"\n\nEdit the content to match your needs.`,
        layout: 'standard'
      } as Block,
      {
        id: `block-${now}-4`,
        type: 'list',
        order: 3,
        items: ['First work item', 'Second work item', 'Third work item'],
        style: 'check'
      } as Block,
      {
        id: `block-${now}-5`,
        type: 'price_table',
        order: 4,
        items: [],
        showQuantity: true,
        showUnitPrice: true
      } as Block,
      {
        id: `block-${now}-6`,
        type: 'terms',
        order: 5,
        title: 'Proposal Terms',
        content: '• Proposal valid for 30 days\n• Payment: 50% deposit, 50% upon completion\n• VAT not included'
      } as Block,
      {
        id: `block-${now}-7`,
        type: 'dual_signature',
        order: 6,
        leftLabel: 'Service Provider Signature',
        rightLabel: 'Client Signature',
        showDate: true
      } as Block
    ]
  }

  // AI Generate - create proposal with AI assistance
  const handleAIGenerate = async (prompt: string) => {
    if (!canUseAI) {
      setShowNewProposalModal(false)
      promptUpgrade('AI generation')
      return
    }
    setShowNewProposalModal(false)
    setIsGeneratingAI(true)

    try {
      const result = await aiService.generateProposal(prompt)

      if (result.success && result.blocks && result.blocks.length > 0) {
        setInitialBlocks(result.blocks as Block[])
      } else {
        setInitialBlocks(createFallbackBlocks(prompt))
      }
    } catch (error) {
      console.error('AI generation failed:', error)
      setInitialBlocks(createFallbackBlocks(
        prompt,
        'AI proposal generation failed. Please edit the proposal manually.'
      ))
    } finally {
      setIsGeneratingAI(false)
    }

    setEditingProposalId(null)
    setInitialClientMeta(null)
    setCurrentView('editor')
  }

  const handleSaveAsTemplate = (blocks: Block[], name: string, description: string, color: string) => {
    addSavedTemplate({
      name,
      description,
      blocks,
      color,
      icon: 'Star'
    })
  }

  const handleUseTemplate = (blocks: Block[]) => {
    setInitialBlocks(blocks)
    setEditingProposalId(null)
    setCurrentView('editor')
  }

  const handleEditProposal = (proposalId: string) => {
    setInitialBlocks([])
    setEditingProposalId(proposalId)
    setCurrentView('editor')
  }

  const handleNavigate = (view: AppView) => {
    setCurrentView(view)
  }

  // Create proposal from client card
  const handleCreateProposalFromClient = (client: Client) => {
    setInitialClientMeta({
      clientName: client.name || '',
      clientEmail: client.email || '',
      clientCompany: client.company || '',
      clientId: client.id
    })
    setInitialBlocks([])
    setEditingProposalId(null)
    setCurrentView('editor')
  }

  // View client profile
  const handleViewClientProfile = (client: Client) => {
    setSelectedClientId(client.id)
    setCurrentView('client-profile')
  }

  if (loading || isGeneratingAI) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center" dir="ltr">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-accent-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">
            {isGeneratingAI ? 'Creating proposal with AI...' : 'Loading...'}
          </p>
          {isGeneratingAI && (
            <p className="text-gray-400 text-sm mt-2">This may take a few seconds</p>
          )}
        </div>
      </div>
    )
  }

  if (!user) {
    return <LandingPage />
  }

  // Onboarding wizard for first-time users
  if (showOnboarding) {
    return (
      <OnboardingWizard
        userEmail={user.email || ''}
        onComplete={handleOnboardingComplete}
      />
    )
  }

  // Editor view - full screen without sidebar
  if (currentView === 'editor') {
    return (
      <Suspense fallback={<PageSpinner />}>
        <ProposalEditor
          onBack={() => {
            setInitialClientMeta(null)
            setCurrentView('dashboard')
          }}
          initialBlocks={initialBlocks}
          proposalId={editingProposalId || undefined}
          onSaveAsTemplate={handleSaveAsTemplate}
          initialMeta={initialClientMeta || undefined}
        />
      </Suspense>
    )
  }

  // Client profile view - full screen without sidebar
  if (currentView === 'client-profile' && selectedClientId) {
    return (
      <Suspense fallback={<PageSpinner />}>
        <ClientProfilePage
          clientId={selectedClientId}
          onBack={() => {
            setSelectedClientId(null)
            setCurrentView('clients')
          }}
          onCreateProposal={handleCreateProposalFromClient}
          onEditProposal={handleEditProposal}
        />
      </Suspense>
    )
  }

  // All other views with sidebar layout
  return (
    <>
      <AppLayout
        currentView={currentView}
        onNavigate={handleNavigate}
        onNewProposal={handleOpenNewProposalModal}
      >
        {currentView === 'dashboard' && (
          <DashboardContent onNewProposal={handleNewProposal} onEditProposal={handleEditProposal} />
        )}
        {currentView === 'templates' && (
          <TemplatesPage
            onUseTemplate={handleUseTemplate}
            onBack={() => setCurrentView('dashboard')}
          />
        )}
        {currentView === 'settings' && (
          <Suspense fallback={<PageSpinner />}><SettingsPage /></Suspense>
        )}
        {currentView === 'profile' && (
          <Suspense fallback={<PageSpinner />}><ProfilePage /></Suspense>
        )}
        {currentView === 'clients' && (
          <Suspense fallback={<PageSpinner />}>
            <ClientsPage
              onCreateProposal={handleCreateProposalFromClient}
              onViewProfile={handleViewClientProfile}
            />
          </Suspense>
        )}
        {currentView === 'billing' && (
          <Suspense fallback={<PageSpinner />}><BillingPage /></Suspense>
        )}
        {currentView === 'admin' && (
          <Suspense fallback={<PageSpinner />}><AdminDashboard /></Suspense>
        )}
        {currentView === 'email' && (
          <Suspense fallback={<PageSpinner />}><InboxPage /></Suspense>
        )}
        {currentView === 'integrations' && (
          <Suspense fallback={<PageSpinner />}><ConnectPage /></Suspense>
        )}
      </AppLayout>

      {/* New Proposal Selection Modal */}
      <NewProposalModal
        isOpen={showNewProposalModal}
        onClose={() => setShowNewProposalModal(false)}
        onStartFromScratch={handleStartFromScratch}
        onUseTemplate={handleGoToTemplates}
        onAIGenerate={handleAIGenerate}
      />

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
      />
    </>
  )
}

// Dashboard content without its own sidebar
function DashboardContent({
  onNewProposal,
  onEditProposal
}: {
  onNewProposal: (blocks?: Block[]) => void
  onEditProposal: (proposalId: string) => void
}) {
  return (
    <Dashboard
      onNewProposal={onNewProposal}
      onEditProposal={onEditProposal}
      onOpenTemplates={() => {}}
      hideSidebar
    />
  )
}

// Login route - redirects to app if already authenticated
function LoginRoute() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center" dir="ltr">
        <div className="w-12 h-12 border-4 border-accent-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (user) {
    return <Navigate to="/" replace />
  }

  return <LoginPage onSuccess={() => navigate('/')} />
}

function App() {
  return (
    <LocaleProvider>
      <BrowserRouter>
        <AuthProvider>
          <SettingsProvider>
            <SubscriptionProvider>
              <Toaster position="top-center" richColors />
              {/* One-time Privacy Consent Popup */}
              <PrivacyConsentPopup />
              <ErrorBoundary>
                <Routes>
                  {/* Public proposal viewing route */}
                  <Route
                    path="/view/:shareToken"
                    element={
                      <ErrorBoundary>
                        <Suspense fallback={<PageSpinner />}>
                          <PublicProposalView />
                        </Suspense>
                      </ErrorBoundary>
                    }
                  />

                  {/* Legal pages (public) */}
                  <Route path="/privacy" element={<Suspense fallback={<PageSpinner />}><PrivacyPage /></Suspense>} />
                  <Route path="/terms" element={<Suspense fallback={<PageSpinner />}><TermsPage /></Suspense>} />
                  <Route path="/accessibility" element={<Suspense fallback={<PageSpinner />}><AccessibilityPage /></Suspense>} />

                  {/* SEO landing / resource pages (public) */}
                  <Route path="/blog/how-to-write-a-proposal" element={<Suspense fallback={<PageSpinner />}><HatatMehirPage /></Suspense>} />
                  <Route path="/blog/digital-signatures" element={<Suspense fallback={<PageSpinner />}><HatimaDigitalitPage /></Suspense>} />
                  <Route path="/templates" element={<Suspense fallback={<PageSpinner />}><ProposalTemplatePage /></Suspense>} />

                  {/* Landing page - always accessible */}
                  <Route path="/welcome" element={<LandingPage />} />

                  {/* Login page */}
                  <Route path="/login" element={<LoginRoute />} />

                  {/* Protected app routes (shows LandingPage when not authenticated) */}
                  <Route path="/*" element={<ProtectedApp />} />
                </Routes>
              </ErrorBoundary>
            </SubscriptionProvider>
          </SettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </LocaleProvider>
  )
}

export default App
